import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";

import Contract from "../../entities/Contract";
import ContractPosition from "../../entities/ContractPosition";
import ContractTypePageSetup from "../../entities/ContractTypePageSetup";
import PayRate from "../../entities/PayRate";
import i18n from "../../i18n";
import ContractStatus from "../../util/config/contractStatus";
import SystemContractType from "../../util/config/contractSystemType";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import ResponseBase from "../types/ResponseBase";
import SliceCollectionBase from "../types/SliceCollectionBase";
import { authHeader, getWorkerId, refreshToken } from "../users/auth";
import { getEnumValueByKeySafe } from "components/utils";
import ContractDetails from "entities/ContractDetails";
import { LocalizedSelectItem } from "entities/LocalizedSelectItem";
import KeyValueItem from "entities/KeyValueItem";


// RESPONSE DECLARATIONS //
interface ContractResponse extends ResponseBase {
    CompanyId: number;
    WorkerName: string;
    CompanyName: string;
    ContractNumber: string;
    ContractPositions: ContractPositionResponse[];
    ContractTypeName: string;
    EndDate?: Date;
    IsMainOccupation: boolean;
    IsManager: boolean; //TODO: This is not implemented yet on backend as of 07.2024
    PaymentDate: number;
    PaymentMonth: number;
    ProbationEndDate?: Date;
    StartDate?: Date | string;
    Status?: keyof typeof ContractStatus;
    SystemType: SystemContractType;
    StartOfEmployment: Date | string;
    ContractDate: Date | string;
    DocuRef?: string;
}

interface ContractPositionResponse {
    CompanyId?: number;
    ContractId: number;
    ContractPositionId: number; //This is Id
    Department: string;
    EndDate?: Date;
    IsPrimary: boolean;
    ManagerId?: number;
    ManagerName?: string;
    OrganizationUnitId?: number;
    PositionId?: number;
    PositionName: string;
    StartDate?: Date;
    WorkerId?: number;
    WorkerName: string;
    Workload?: number;
}


interface PayRateResponse {
    Amount?: number;
    Code: string;
    EndDate?: Date;
    FullTimeAmount?: number;
    Id?: number; //Here Id is optional
    Name: string;
    StartDate?: Date;
}

interface ContractTypePageSetupResponse {
    CompanyId: number;
    ContractTypePageSetupId: number; //This is Id
    Enabled: boolean;
    SelfServicePageCode: string;
    SelfServicePageName: string;
    SystemContractType: number;
}

interface LocalizedContractTypeResponse {
    TypeId: number;
    Value: string;
    TypeDescription: number;
    Locale: string;
}

interface KeyValueResponse {
    Id: number,
    Name: string
}

interface ContractDetailsResponse extends ResponseBase {
    ContractNumber: string,
    Salary?: number,
    MonthlyBasicSalary?: number,
    Workload?: number,
    BusinessSecretFine?: number,
    HourlyRate?: number,
    NonCompeteFine?: number,
    JobDuties?: string,
    ContractTypeId: number,
    Currency: string,
    ModeOfOperationId?: number,
    ModeOfOperationName?: string,
    EmploymentRelationId?: number,
    RemoteWorkId?: number,
    RemoteWorkName?: string,
    StartOfContract?: Date,
    EndOfContract?: Date,
    CollectiveAgreementEnd?: Date,
    SalaryStartDate?: Date,
    StartOfEmployment: Date,
    EndOfProbation?: Date,
    IsMain: boolean,
    Countries: KeyValueResponse[],
    ForbiddenActivities: KeyValueResponse[],
    CompetitionRestrictionLength?: number,
    CompetitionRestrictionPercentage?: number,
    NightHourlyRate?: number,
    HolidayHourlyRate?: number,
    OvertimeHourlyRate?: number,
}

// SLICE //
interface State {
    workerContracts: SliceCollectionBase<Contract>;
    subordinateContracts: SliceCollectionBase<Contract>;
    selectedContract: {
        data: Contract | null;
        isManager: boolean;
        contractChanged: number | null;
    };
    contractPositions: {
        requestedOrganizationUnits: number[];
    } & SliceCollectionBase<ContractPosition>;
    payrates: SliceCollectionBase<PayRate>;
    // systemTypes: SliceSingletonBase<{ [key: number]: string }>, //TODO: This doesnt seem to be in use anywhere. Remove if not needed
    contractTypePageSetup: SliceCollectionBase<ContractTypePageSetup>;
    contractDetails: SliceCollectionBase<ContractDetails>;
    localizedContractTypes: SliceCollectionBase<LocalizedSelectItem>;
    companyCalendars: SliceCollectionBase<KeyValueItem>;
}

const initialState: State = {
    workerContracts: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    subordinateContracts: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    selectedContract: {
        data: null,
        isManager: false,
        contractChanged: null
    },
    contractPositions: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null,
        requestedOrganizationUnits: []
    },
    payrates: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    contractTypePageSetup: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    contractDetails: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    localizedContractTypes: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },
    companyCalendars: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    }
};

const slice = createSlice({
    name: "contracts",
    initialState,
    reducers: {
        // REQUESTED WORKER ALL CONTRACTS
        contractsRequested: contracts => {
            contracts.workerContracts.loading = true;
        },
        contractsReceived: (
            contracts,
            action: PayloadAction<ContractResponse[]>
        ) => {
            contracts.workerContracts.list = contractsSchema(action.payload);
            contracts.workerContracts.loading = false;
            contracts.workerContracts.lastFetch = Date.now();
        },
        contractsRequestFailed: (
            contracts,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            contracts.workerContracts.loading = false;
            contracts.workerContracts.error = action.payload;
        },

        subordinateContractsRequested: contracts => {
            contracts.subordinateContracts.loading = true;
        },
        subordinateContractsReceived: (
            contracts,
            action: PayloadAction<ContractResponse[]>
        ) => {
            contracts.subordinateContracts.list = contractsSchema(action.payload);
            contracts.subordinateContracts.loading = false;
            contracts.subordinateContracts.lastFetch = Date.now();
        },
        subordinateContractsRequestFailed: (
            contracts,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            contracts.subordinateContracts.loading = false;
            contracts.subordinateContracts.error = action.payload;
        },

        // SET WORKER MAIN CONTRACT
        selectedContractReplaced: (contracts, action: PayloadAction<Contract>) => {
            contracts.selectedContract.data = action.payload;
            contracts.selectedContract.isManager = action.payload.IsManager;
            contracts.selectedContract.contractChanged = Date.now();
        },

        // REQUESTED PAYRATES
        payratesRequested: contracts => {
            contracts.payrates.loading = true;
            contracts.payrates.error = null;
        },
        payratesReceived: (contracts, action: PayloadAction<PayRateResponse[]>) => {
            contracts.payrates.list = payRatesSchema(action.payload);
            contracts.payrates.loading = false;
            contracts.payrates.lastFetch = Date.now();
        },
        payratesRequestFailed: (
            contracts,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            contracts.payrates.loading = false;
            contracts.payrates.error = action.payload;
        },

        // REQUESTED CONTRACT TYPE PAGE SETUP
        contractTypePageSetupRequested: contracts => {
            contracts.contractTypePageSetup.loading = true;
            contracts.contractTypePageSetup.error = null;
        },
        contractTypePageSetupReceived: (
            contracts,
            action: PayloadAction<ContractTypePageSetupResponse[]>
        ) => {
            contracts.contractTypePageSetup.list = pageSetupSchema(action.payload);
            contracts.contractTypePageSetup.loading = false;
            contracts.contractTypePageSetup.lastFetch = Date.now();
        },
        contractTypePageSetupRequestFailed: (
            contracts,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            contracts.contractTypePageSetup.loading = false;
            contracts.contractTypePageSetup.error = action.payload;
        },

        // REQUESTED CONTRACT POSITIONS BY UNIT ID-S
        contractPositionsRequested: contracts => {
            contracts.contractPositions.loading = true;
            contracts.contractPositions.error = null;
        },
        contractPositionsReceived: (
            contracts,
            action: PayloadAction<ContractPositionResponse[]>
        ) => {
            const newPostions = contractPositionsSchema(action.payload);
            const positions = [...contracts.contractPositions.list];

            if (Array.isArray(newPostions) && newPostions.length > 0) {
                const existingIds = new Map(positions.map(pos => [pos.Id, pos]));
                newPostions.forEach(balance => {
                    if (!existingIds.has(balance.Id)) {
                        positions.push(balance);
                        existingIds.set(balance.Id, balance);
                    }
                });
            }

            contracts.contractPositions.list = positions;
            contracts.contractPositions.loading = false;
            contracts.contractPositions.lastFetch = Date.now();
        },
        contractPositionsRequestFailed: (
            contracts,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            contracts.contractPositions.loading = false;
            contracts.contractPositions.error = action.payload;
        },

        // CONTRACT OCCUPATION UPDATE
        workerOccupationUpdated: (
            contracts,
            action: PayloadAction<ContractResponse>
        ) => {
            const currentContract = contracts.selectedContract.data;

            if (currentContract) {
                currentContract.IsMainOccupation = true;
                contracts.selectedContract.data = currentContract;
            }

            contracts.workerContracts.list = contracts.workerContracts.list.map(
                contract =>
                    contract.Id === action.payload.Id
                        ? { ...contract, IsMainOccupation: true }
                        : { ...contract, IsMainOccupation: false }
            );
        },

        // KEEP TRACK OF ORG UNIT IDS TO ADD ONLY NOT REQUESTED ORG UNIT - FOR PERFORMANCE REASONS
        requestedOrganizationUnitsUpdated: (contracts, action) => {
            const currentUnits = contracts.contractPositions.requestedOrganizationUnits;

            const newUnitId = action.payload;
            if (!currentUnits.some(existingUnitId => existingUnitId === newUnitId)) {
                contracts.contractPositions.requestedOrganizationUnits = [...currentUnits, newUnitId];
            }
        },

        localizedContractTypesRequested: contracts => {
            contracts.localizedContractTypes.loading = true;
            contracts.localizedContractTypes.error = null;
        },
        localizedContractTypesReceived: (
            contracts,
            action: PayloadAction<LocalizedContractTypeResponse[]>
        ) => {
            contracts.localizedContractTypes.list = typeSchema(action.payload);
            contracts.localizedContractTypes.loading = false;
            contracts.localizedContractTypes.lastFetch = Date.now();
        },
        localizedContractTypesRequestFailed: (
            contracts,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            contracts.localizedContractTypes.loading = false;
            contracts.localizedContractTypes.error = action.payload;
        },

        contractDetailsRequested: contracts => {
            contracts.contractDetails.loading = true;
            contracts.contractDetails.error = null;
        },
        contractDetailsReceived: (
            contracts,
            action: PayloadAction<ContractDetailsResponse[]>
        ) => {
            contracts.contractDetails.list = contractDetailSchema(action.payload);
            contracts.contractDetails.loading = false;
            contracts.contractDetails.lastFetch = Date.now();
        },
        contractDetailsRequestFailed: (
            contracts,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            contracts.contractDetails.loading = false;
            contracts.contractDetails.error = action.payload;
        },
        contractDetailsUpdated: (contracts,
            action: PayloadAction<ContractDetailsResponse>) => {
            const index = contracts.contractDetails.list.findIndex(d => d.Id === action.payload.Id);
            if (index !== -1) {
                contracts.contractDetails.list[index] = mapDetails(action.payload);
            }
        },
        companyCalendarsRequested: contracts => {
            contracts.companyCalendars.loading = true;
            contracts.companyCalendars.error = null;
        },
        companyCalendarsReceived: (
            contracts,
            action: PayloadAction<KeyValueResponse[]>
        ) => {
            contracts.companyCalendars.list = companyCalendarsSchema(action.payload);
            contracts.companyCalendars.loading = false;
            contracts.companyCalendars.lastFetch = Date.now();
        },
        companyCalendarsRequestFailed: (
            contracts,
            action: PayloadAction<ApiCallFailedPayload>
        ) => {
            contracts.companyCalendars.loading = false;
            contracts.companyCalendars.error = action.payload;
        },
    }
});

const {
    contractsRequested,
    contractsReceived,
    contractsRequestFailed,

    subordinateContractsRequested,
    subordinateContractsReceived,
    subordinateContractsRequestFailed,

    selectedContractReplaced,

    payratesRequested,
    payratesReceived,
    payratesRequestFailed,

    contractTypePageSetupRequested,
    contractTypePageSetupReceived,
    contractTypePageSetupRequestFailed,

    contractPositionsRequested,
    contractPositionsReceived,
    contractPositionsRequestFailed,

    workerOccupationUpdated,

    localizedContractTypesRequested,
    localizedContractTypesReceived,
    localizedContractTypesRequestFailed,

    contractDetailsRequested,
    contractDetailsReceived,
    contractDetailsRequestFailed,
    contractDetailsUpdated,

    companyCalendarsRequested,
    companyCalendarsReceived,
    companyCalendarsRequestFailed
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const getAllContracts =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.contracts.workerContracts.loading) {
            return;
        }

        const workerId = getWorkerId(getState());
        const url =
            "/api/contracts/worker/" + workerId + "/" + i18n.language + "/all";

        const { lastFetch } = getState().entities.contracts.workerContracts;

        const diffInMinutes = moment().diff(moment(lastFetch), "minutes");
        if (
            diffInMinutes <
            parseInt((window._env_.REACT_APP_COMMON_REQUEST_INTERVAL || process.env.REACT_APP_COMMON_REQUEST_INTERVAL)!, 10)
        ) {
            return;
        }

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },
                onStart: contractsRequested.type,
                onSuccess: contractsReceived.type,
                onError: contractsRequestFailed.type
            })
        );
    };

export const getSubordinateContracts =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.contracts.subordinateContracts.loading) {
            return;
        }

        const workerId = getWorkerId(getState());
        const url =
            "/api/contracts/worker/" + workerId + "/" + i18n.language + "/subordinates";

        const { lastFetch } = getState().entities.contracts.subordinateContracts;

        const diffInMinutes = moment().diff(moment(lastFetch), "minutes");
        if (
            diffInMinutes <
            parseInt((window._env_.REACT_APP_COMMON_REQUEST_INTERVAL || process.env.REACT_APP_COMMON_REQUEST_INTERVAL)!, 10)
        ) {
            return;
        }

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },
                onStart: subordinateContractsRequested.type,
                onSuccess: subordinateContractsReceived.type,
                onError: subordinateContractsRequestFailed.type
            })
        );
    };


export const setWorkerContract =
    (contractId: number) => (dispatch: AppDispatch, getState: () => AppState) => {
        const state = getState();
        if (
            state.entities.contracts.selectedContract.data?.Id === contractId ||
            state.entities.contracts.workerContracts.loading
        ) {
            return;
        }
        const currentContract = state.entities.contracts.selectedContract.data;

        if (!contractId || currentContract?.Id === contractId) {
            return;
        }

        const { list: contracts } = state.entities.contracts.workerContracts;
        const newContract = contracts?.find(c => c.Id === contractId);

        if (newContract) {
            const newContractClone = { ...newContract };

            if (
                Array.isArray(newContractClone.ContractPositions) &&
                newContractClone.ContractPositions.length > 0
            ) {
                newContractClone.ContractPositions = [
                    newContractClone.ContractPositions[0]
                ];
            }

            dispatch({
                type: selectedContractReplaced.type,
                payload: newContractClone
            });

            if (currentContract && currentContract.Id !== newContractClone.Id) {
                dispatch(refreshToken());
            }
        }
    };

export const setWorkerContractPosition =
    (contractPositionId?: number) =>
        (dispatch: AppDispatch, getState: () => AppState) => {
            const currentContract = getState().entities.contracts.selectedContract.data;
            const workerId = getWorkerId(getState());
            if (!contractPositionId) return;
            if (
                !contractPositionId ||
                currentContract?.ContractPositions?.[0]?.Id === contractPositionId
            ) {
                return;
            }

            const { list: contracts } = getState().entities.contracts.workerContracts;
            let newContract = null;
            let foundPosition = null;

            for (const contract of contracts) {
                const position = contract.ContractPositions?.find(
                    p => p.Id === contractPositionId
                );
                if (position) {
                    newContract = { ...contract };
                    foundPosition = position;
                    break;
                }
            }

            if (newContract && foundPosition) {
                newContract.ContractPositions = [foundPosition];
                dispatch({ type: selectedContractReplaced.type, payload: newContract });

                //To save contract change into WorkerSettings table 
                const url = "/api/contracts/worker/" + workerId + "/" + foundPosition.Id + "/" + i18n.language;
                dispatch(apiCallBegan({
                    url,
                    method: "GET",
                    headers: { ...authHeader(), "Accept-Language": i18n.language },
                }))


                if (currentContract && currentContract.Id !== newContract.Id) {
                    dispatch(refreshToken());
                }
            }
        };

export const getPayrates =
    (contractId: number) =>
        async (dispatch: AppDispatch, getState: () => AppState) => {
            if (getState().entities.contracts.payrates.loading) {
                return;
            }

            const url = "/api/contracts/payrate/" + contractId;

            return await dispatch(
                apiCallBegan({
                    url,
                    method: "GET",
                    headers: { ...authHeader(), "Accept-Language": i18n.language },
                    onStart: payratesRequested.type,
                    onSuccess: payratesReceived.type,
                    onError: payratesRequestFailed.type
                })
            );
        };



export const getContractTypePageSetup =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.contracts.contractTypePageSetup.loading) {
            return;
        }

        const url = "/api/contracts/pagesetup/0";

        const { lastFetch } = getState().entities.contracts.contractTypePageSetup;

        const diffInMinutes = moment().diff(moment(lastFetch), "minutes");
        if (
            diffInMinutes <
            parseInt((window._env_.REACT_APP_COMMON_REQUEST_INTERVAL || process.env.REACT_APP_COMMON_REQUEST_INTERVAL)!, 10)
        ) {
            return;
        }

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },
                onStart: contractTypePageSetupRequested.type,
                onSuccess: contractTypePageSetupReceived.type,
                onError: contractTypePageSetupRequestFailed.type
            })
        );
    };

export const getLocalizedContractTypes =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.contracts.localizedContractTypes.loading) {
            return;
        }

        const url = "/api/contracts/contracttypes/";

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },

                onStart: localizedContractTypesRequested.type,
                onSuccess: localizedContractTypesReceived.type,
                onError: localizedContractTypesRequestFailed.type
            })
        );
    }

export const getCompanyCalendars =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.contracts.companyCalendars.loading) {
            return;
        }

        const companyId = getState().users.auth.data?.CompanyId ?? 0;

        const url = `/api/contracts/company/${companyId}/calendars/`;

        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },

                onStart: companyCalendarsRequested.type,
                onSuccess: companyCalendarsReceived.type,
                onError: companyCalendarsRequestFailed.type
            })
        );
    }
export const getUsersContractDetails =
    () => async (dispatch: AppDispatch, getState: () => AppState) => {
        if (getState().entities.contracts.contractDetails.loading) {
            return;
        }

        const workerId = getWorkerId(getState());

        const url = `/api/contracts/contractdetails/user/${workerId}`;


        return await dispatch(
            apiCallBegan({
                url,
                method: "GET",
                headers: { ...authHeader(), "Accept-Language": i18n.language },

                onStart: contractDetailsRequested.type,
                onSuccess: contractDetailsReceived.type,
                onError: contractDetailsRequestFailed.type
            })
        );
    }

export const updateContractDetails = (details: ContractDetails) => async (dispatch: AppDispatch, getState: () => AppState) => {
    await dispatch(apiCallBegan({
        url: `/api/contracts/contractdetails/${details.Id}`,
        method: "put",
        data: details,
        headers: {
            ...authHeader(),
            Accept: "application/json",
            "Content-Type": "application/json",
            "Accept-Language": i18n.language
        },
        onSuccess: contractDetailsUpdated.type,
    }));
}

export const getContractPositionsByUnitIds = (organizationUnitIds?: number[]) =>
    async (dispatch: AppDispatch, getState: () => AppState) => {
        if (!Array.isArray(organizationUnitIds) || getState().entities.contracts.contractDetails.loading) {
            return;
        }

        const url = `/api/contracts/contractpositions`;

        return await dispatch(
            apiCallBegan({
                url,
                method: "POST",
                headers: {
                    ...authHeader(),
                    "Accept-Language": i18n.language,
                    "Content-Type": "application/json"
                },
                data: organizationUnitIds,
                onStart: contractPositionsRequested.type,
                onSuccess: contractPositionsReceived.type,
                onError: contractPositionsRequestFailed.type
            })
        );
    };

export const updateContractOccupation = (contractId: number) =>
    apiCallBegan({
        url: `/api/contracts/setmainoccupation/${contractId}`,
        method: "put",
        headers: {
            ...authHeader(),
            Accept: "application/json",
            "Content-Type": "application/json",
            "Accept-Language": i18n.language
        },
        onSuccess: workerOccupationUpdated.type
    });

// NORMALIZATION //
const contractsSchema = (payload: ContractResponse[]): Contract[] => {
    if (!payload || payload.length === 0) {
        return [];
    }

    return payload.map(
        (resp: ContractResponse): Contract => ({
            WorkerName: resp.WorkerName,
            CompanyId: resp.CompanyId,
            CompanyName: resp.CompanyName,
            ContractNumber: resp.ContractNumber,
            ContractPositions: contractPositionsSchema(resp.ContractPositions),
            ContractTypeName: resp.ContractTypeName,
            EndDate: resp.EndDate,
            Id: resp.Id,
            IsMainOccupation: resp.IsMainOccupation,
            IsManager: resp.IsManager, //TODO: This is not implemented yet on backend as of 07.2024
            PaymentDate: resp.PaymentDate,
            PaymentMonth: resp.PaymentMonth,
            ProbationEndDate: resp.ProbationEndDate,
            StartDate: resp.StartDate,
            Status: getEnumValueByKeySafe(ContractStatus, resp.Status),
            SystemType: resp.SystemType,
            SeniorityDate: resp.StartOfEmployment,
            ContractDate: resp.ContractDate,
            DocuRef: resp.DocuRef,
        })
    );
};

const contractPositionsSchema = (
    payload: ContractPositionResponse[]
): ContractPosition[] => {
    if (!payload || payload.length === 0) {
        return [];
    }

    return payload.map(
        (resp: ContractPositionResponse): ContractPosition => ({
            CompanyId: resp.CompanyId,
            ContractId: resp.ContractId,
            Id: resp.ContractPositionId,
            EndDate: resp.EndDate,
            IsPrimary: resp.IsPrimary,
            ManagerId: resp.ManagerId,
            ManagerName: resp.ManagerName,
            OrganizationUnitId: resp.OrganizationUnitId,
            OrganizationUnitName: resp.Department,
            PositionId: resp.PositionId,
            PositionName: resp.PositionName,
            StartDate: resp.StartDate,
            WorkerId: resp.WorkerId,
            WorkerName: resp.WorkerName,
            Workload: resp.Workload
        })
    );
};

const payRatesSchema = (payload: PayRateResponse[]): PayRate[] => {
    if (!payload || payload.length === 0) {
        return [];
    }

    return payload
        .filter((resp: PayRateResponse) => resp.Id) // Filter out objects without Id, as Id is required actually
        .map(
            (resp: PayRateResponse): PayRate => ({
                Amount: resp.Amount,
                Code: resp.Code,
                EndDate: resp.EndDate,
                FullTimeAmount: resp.FullTimeAmount,
                Id: resp.Id!,
                Name: resp.Name,
                StartDate: resp.StartDate
            })
        );
};

const typeSchema = (payload: LocalizedContractTypeResponse[]): LocalizedSelectItem[] => {
    if (!payload || payload.length === 0) {
        return [];
    }

    return payload.map(
        (resp: LocalizedContractTypeResponse): LocalizedSelectItem => ({
            typeId: resp.TypeId,
            value: resp.Value,
            type: resp.TypeDescription,
            locale: resp.Locale
        }));
}

const companyCalendarsSchema = (payload: KeyValueResponse[]): KeyValueItem[] => {
    if (!payload || payload.length === 0) {
        return [];
    }

    return payload.map(
        (resp: KeyValueResponse): KeyValueItem => ({
            id: resp.Id,
            value: resp.Name,
        }));
}

const contractDetailSchema = (payload: ContractDetailsResponse[]): ContractDetails[] => {
    if (!payload || payload.length === 0) {
        return [];
    }

    return payload.map(
        (resp: ContractDetailsResponse): ContractDetails => mapDetails(resp));
}

const mapDetails = (resp: ContractDetailsResponse): ContractDetails => {
    return {
        Id: resp.Id,
        contractNumber: resp.ContractNumber,
        contractTypeId: resp.ContractTypeId,
        currency: resp.Currency,
        startOfContract: resp.StartOfContract,
        startOfEmployment: resp.StartOfEmployment,
        endOfContract: resp.EndOfContract,
        salary: resp.Salary,
        monthlyBasicSalary: resp.MonthlyBasicSalary,
        hourlyRate: resp.HourlyRate,
        salaryStartDate: resp.SalaryStartDate,
        endOfProbation: resp.EndOfProbation,
        workload: resp.Workload,
        isMain: resp.IsMain,
        jobDuties: resp.JobDuties,
        remoteWorkId: resp.RemoteWorkId,
        remoteWorkName: resp.RemoteWorkName,
        businessSecretFine: resp.BusinessSecretFine,
        nonCompeteFine: resp.NonCompeteFine,
        collectiveAgreementEnd: resp.CollectiveAgreementEnd,
        countries: resp.Countries.map((item: KeyValueResponse): KeyValueItem => { return { id: item.Id, value: item.Name } }),
        forbiddenActivities: resp.ForbiddenActivities.map((item: KeyValueResponse): KeyValueItem => { return { id: item.Id, value: item.Name } }),
        competitionRestrictionLength: resp.CompetitionRestrictionLength,
        competitionRestrictionPercentage: resp.CompetitionRestrictionPercentage,
        nightHourlyRate: resp.NightHourlyRate,
        overtimeHourlyRate: resp.OvertimeHourlyRate,
        holidayHourlyRate: resp.HolidayHourlyRate,
        modeOfOperationId: resp.ModeOfOperationId,
        modeOfOperationName: resp.ModeOfOperationName
    }
}

const pageSetupSchema = (
    payload: ContractTypePageSetupResponse[]
): ContractTypePageSetup[] => {
    if (!payload || payload.length === 0) {
        return [];
    }

    return payload.map(
        (resp: ContractTypePageSetupResponse): ContractTypePageSetup => ({
            CompanyId: resp.CompanyId,
            Enabled: resp.Enabled,
            Id: resp.ContractTypePageSetupId,
            SelfServicePageCode: resp.SelfServicePageCode,
            SelfServicePageName: resp.SelfServicePageName,
            SystemContractType: resp.SystemContractType
        })
    );
};
